import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SectionTitle, Spinner, Empty } from "@/components/common";
import { toast } from "sonner";
import { Plug, Plus, PlugZap, Play, Code2, Download, Trash2, Loader2, CheckCircle2, XCircle, Pencil, FileCode2, Save, Activity, RefreshCw, HelpCircle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const STYLES = [
  { v: "odata", label: "OData / REST (S/4HANA Cloud & gateway)" },
  { v: "bapi", label: "BAPI / RFC (on-prem ECC / S/4)" },
  { v: "idoc", label: "IDoc (XML exchange)" },
];
const SYSTEMS = ["S/4HANA Cloud", "S/4HANA On-Premise", "SAP ECC", "SAP BTP", "Other"];
const AUTHS = [
  { v: "oauth_client_credentials", label: "OAuth 2.0 (client credentials)" },
  { v: "basic", label: "Basic auth (user / password)" },
  { v: "none", label: "None" },
];

const blank = {
  name: "", call_style: "odata", system_type: "S/4HANA Cloud", base_url: "", service_path: "",
  sap_client: "", sysnr: "00", auth_type: "oauth_client_credentials",
  client_id: "", client_secret: "", token_url: "", username: "", password: "", idoc_endpoint: "",
};

export default function SapConnectors() {
  const { data: connectors, refetch, isLoading } = useQuery({ queryKey: ["connectors"], queryFn: async () => (await api.get("/connectors")).data });
  const { data: health, refetch: refetchHealth } = useQuery({ queryKey: ["connectors-health"], queryFn: async () => (await api.get("/connectors/health")).data, refetchInterval: 60000 });
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState(null);           // create/edit dialog state
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);     // connector id being tested
  const [runFor, setRunFor] = useState(null);       // connector for the execute dialog
  const [codeFor, setCodeFor] = useState(null);
  const [code, setCode] = useState(null);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditId(null); setForm({ ...blank }); };
  const openEdit = async (c) => {
    const full = (await api.get(`/connectors/${c.id}`)).data;
    setEditId(c.id);
    setForm({ ...blank, ...full, client_secret: "", password: "" });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the connector a name"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/connectors/${editId}`, form);
      else await api.post("/connectors", form);
      toast.success(editId ? "Connector updated" : "Connector created");
      setForm(null); setEditId(null); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async (c) => {
    setTesting(c.id);
    try {
      const r = (await api.post(`/connectors/${c.id}/test`)).data;
      if (r.ok) toast.success(`${c.name}: ${r.detail}${r.latency_ms ? ` (${r.latency_ms} ms)` : ""}`);
      else toast.error(`${c.name}: ${r.detail}`, { duration: 7000 });
      refetch(); refetchHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(null); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete connector "${c.name}"?`)) return;
    await api.delete(`/connectors/${c.id}`); toast.success("Deleted"); refetch(); refetchHealth();
  };

  const checkAll = async () => {
    setChecking(true);
    try {
      const r = (await api.post("/connectors/health/check-all")).data;
      toast.success(`Health check complete · ${r.up} up, ${r.down} down`);
      refetch(); refetchHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Health check failed"); }
    finally { setChecking(false); }
  };

  const viewCode = async (c) => {
    setCodeFor(c); setCode(null);
    const r = (await api.get(`/connectors/${c.id}/code`)).data;
    setCode(r.files);
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-7 animate-fade-up max-w-5xl" data-testid="connectors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <SectionTitle eyebrow="Integration" title="SAP Connectors" />
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">Configure a system, test the connection, run OData / BAPI / IDoc calls for testing, and download a ready-to-review connector project. <span className="text-foreground font-medium">Runs are a preview stage</span> — not an SAP transport or auto-deploy.</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="new-connector-btn"><Plus size={16} /> New connector</Button>
      </div>

      {/* Health dashboard */}
      {connectors?.length > 0 && (
        <div className="rounded-xl border bg-card p-5 space-y-4" data-testid="health-dashboard">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2] font-semibold"><Activity size={17} /> Connector health</div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Auto-checked every 15 min · last {timeAgo(health?.checked_at)}</span>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={checkAll} disabled={checking} data-testid="check-all-btn">{checking ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Check all now</Button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["Up", health?.up ?? 0, "text-emerald-600", CheckCircle2], ["Down", health?.down ?? 0, "text-rose-600", XCircle], ["Unchecked", health?.unknown ?? 0, "text-muted-foreground", HelpCircle]].map(([label, val, color, Icon]) => (
              <div key={label} className="rounded-lg border bg-background p-3 text-center" data-testid={`health-${label.toLowerCase()}`}>
                <div className={`flex items-center justify-center gap-1.5 text-2xl font-bold ${color}`}><Icon size={18} /> {val}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {(health?.connectors || []).map((c) => {
              const h = c.health;
              const dot = !h ? "bg-slate-300" : h.ok ? "bg-emerald-500" : "bg-rose-500";
              return (
                <div key={c.id} className="flex items-center gap-3 text-sm py-1.5 border-b last:border-0" data-testid={`health-row-${c.id}`}>
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
                  <span className="font-medium text-foreground truncate flex-1">{c.name} <span className="text-xs text-muted-foreground">({c.call_style})</span></span>
                  <span className="text-xs text-muted-foreground shrink-0">{h ? (h.ok ? "Operational" : "Down") : "Not checked"}{h?.http_status ? ` · ${h.http_status}` : ""}{h?.latency_ms != null ? ` · ${h.latency_ms} ms` : ""}</span>
                  <span className="text-xs text-muted-foreground shrink-0 w-16 text-right">{timeAgo(h?.checked_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!connectors?.length ? (
        <Empty title="No SAP connectors yet" hint="Add a connector with your SAP endpoint and credentials. OData connections are executed live from Business360; BAPI/RFC & IDoc ship as a runnable project." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {connectors.map((c) => (
            <div key={c.id} className="rounded-xl border bg-card p-5 space-y-3" data-testid={`connector-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2]"><Plug size={16} /><span className="font-semibold text-foreground">{c.name}</span></div>
                <span className="text-[10px] uppercase tracking-wide bg-secondary px-2 py-1 rounded font-semibold">{c.call_style}</span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {[["System", c.system_type], ["Auth", c.auth_type], ["Base URL", c.base_url || "—"], ["Service", c.service_path || c.idoc_endpoint || "—"]].map(([k, v]) => (
                  <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground font-medium truncate">{v}</dd></div>
                ))}
              </dl>
              {c.last_test && (
                <div className={`text-xs flex items-center gap-1.5 ${c.last_test.ok ? "text-emerald-600" : "text-rose-600"}`} data-testid={`test-result-${c.id}`}>
                  {c.last_test.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}<span className="truncate">{c.last_test.detail}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => test(c)} disabled={testing === c.id} data-testid={`test-${c.id}`}>
                  {testing === c.id ? <Loader2 className="animate-spin" size={13} /> : <PlugZap size={13} />} Test
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setRunFor(c)} data-testid={`run-${c.id}`}><Play size={13} /> Run</Button>
                <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => viewCode(c)} data-testid={`code-${c.id}`}><Code2 size={13} /> Code</Button>
                <a href={`${API}/connectors/${c.id}/download`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1.5 h-8"><Download size={13} /> .zip</Button></a>
                <Button size="sm" variant="ghost" className="gap-1.5 h-8" onClick={() => openEdit(c)}><Pencil size={13} /></Button>
                <Button size="sm" variant="ghost" className="gap-1.5 h-8 text-rose-600" onClick={() => del(c)} data-testid={`delete-${c.id}`}><Trash2 size={13} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit connector" : "New SAP connector"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => up("name", e.target.value)} placeholder="S/4HANA Sales Order API" data-testid="conn-name" /></div>
                <div><Label>Call style</Label>
                  <Select value={form.call_style} onValueChange={(v) => up("call_style", v)}>
                    <SelectTrigger data-testid="conn-style"><SelectValue /></SelectTrigger>
                    <SelectContent>{STYLES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>System type</Label>
                  <Select value={form.system_type} onValueChange={(v) => up("system_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>SAP client (mandant)</Label><Input value={form.sap_client} onChange={(e) => up("sap_client", e.target.value)} placeholder="100" /></div>
              </div>

              {form.call_style !== "idoc" ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label>{form.call_style === "bapi" ? "Application server host (ashost)" : "Base URL"}</Label><Input value={form.base_url} onChange={(e) => up("base_url", e.target.value)} placeholder={form.call_style === "bapi" ? "sapprd.example.com" : "https://my-s4.example.com"} data-testid="conn-base" /></div>
                  {form.call_style === "odata" && <div className="sm:col-span-2"><Label>Service path</Label><Input value={form.service_path} onChange={(e) => up("service_path", e.target.value)} placeholder="/sap/opu/odata/sap/API_SALES_ORDER_SRV" /></div>}
                  {form.call_style === "bapi" && <div><Label>System number (sysnr)</Label><Input value={form.sysnr} onChange={(e) => up("sysnr", e.target.value)} placeholder="00" /></div>}
                </div>
              ) : (
                <div><Label>IDoc endpoint URL</Label><Input value={form.idoc_endpoint} onChange={(e) => up("idoc_endpoint", e.target.value)} placeholder="https://my-s4.example.com/sap/bc/idoc_xml" data-testid="conn-idoc" /></div>
              )}

              <div><Label>Authentication</Label>
                <Select value={form.auth_type} onValueChange={(v) => up("auth_type", v)}>
                  <SelectTrigger data-testid="conn-auth"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUTHS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.auth_type === "oauth_client_credentials" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label>Token URL</Label><Input value={form.token_url} onChange={(e) => up("token_url", e.target.value)} placeholder="https://auth.example.com/oauth/token" /></div>
                  <div><Label>Client ID</Label><Input value={form.client_id} onChange={(e) => up("client_id", e.target.value)} /></div>
                  <div><Label>Client secret {editId && <span className="text-muted-foreground text-xs">(leave blank to keep)</span>}</Label><Input type="password" value={form.client_secret} onChange={(e) => up("client_secret", e.target.value)} /></div>
                </div>
              )}
              {form.auth_type === "basic" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Username</Label><Input value={form.username} onChange={(e) => up("username", e.target.value)} /></div>
                  <div><Label>Password {editId && <span className="text-muted-foreground text-xs">(leave blank to keep)</span>}</Label><Input type="password" value={form.password} onChange={(e) => up("password", e.target.value)} /></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-connector">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} {editId ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RunDialog connector={runFor} onClose={() => setRunFor(null)} />

      {/* Code viewer */}
      <Dialog open={!!codeFor} onOpenChange={() => setCodeFor(null)}>
        <DialogContent className="max-w-3xl h-[82vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileCode2 size={18} /> {codeFor?.name} — Connector project</DialogTitle></DialogHeader>
          {!code ? <Spinner label="Generating…" /> : (
            <div className="overflow-y-auto space-y-4 pr-2">
              {Object.entries(code).map(([name, content]) => (
                <div key={name}>
                  <div className="text-xs font-mono font-semibold text-[#29abe2] mb-1">{name}</div>
                  <pre className="text-[11px] bg-[#0b1329] text-[#dbe4f5] rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">{content}</pre>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RunDialog({ connector, onClose }) {
  const [op, setOp] = useState({ method: "GET", entity: "", payload: "", idempotency_key: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  React.useEffect(() => { if (connector) { setOp({ method: "GET", entity: "", payload: "", idempotency_key: "" }); setResult(null); } }, [connector]);
  if (!connector) return null;
  const isIdoc = connector.call_style === "idoc";

  const run = async () => {
    setBusy(true); setResult(null);
    let payload = op.payload;
    if (!isIdoc && op.method !== "GET" && op.payload) {
      try { payload = JSON.parse(op.payload); } catch { toast.error("Payload must be valid JSON"); setBusy(false); return; }
    }
    try {
      const r = (await api.post(`/connectors/${connector.id}/execute`, { method: op.method, entity: op.entity, payload, idempotency_key: op.idempotency_key || undefined })).data;
      setResult(r);
    } catch (e) { setResult({ ok: false, detail: e?.response?.data?.detail || "Execution failed" }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!connector} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="run-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Play size={16} /> Run via {connector.name} <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#c9a227]/15 text-[#a6821a]">PREVIEW</span></DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-xs rounded-lg border border-dashed p-2.5 text-muted-foreground">Executes an API call against the configured endpoint for testing. This is <span className="font-medium text-foreground">not</span> an SAP transport or a deployment into your system.</div>
          {isIdoc ? (
            <div><Label>IDoc XML payload</Label><Textarea rows={8} value={op.payload} onChange={(e) => setOp({ ...op, payload: e.target.value })} placeholder="<?xml ...><ORDERS05>…</ORDERS05>" data-testid="run-payload" className="font-mono text-xs" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Method</Label>
                  <Select value={op.method} onValueChange={(v) => setOp({ ...op, method: v })}>
                    <SelectTrigger data-testid="run-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{["GET", "POST", "PATCH", "PUT"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>{connector.call_style === "bapi" ? "BAPI / function module" : "Entity set / path"}</Label><Input value={op.entity} onChange={(e) => setOp({ ...op, entity: e.target.value })} placeholder={connector.call_style === "bapi" ? "BAPI_SALESORDER_CREATEFROMDAT2" : "A_SalesOrder"} data-testid="run-entity" /></div>
              </div>
              {op.method !== "GET" && (
                <div><Label>Payload (JSON)</Label><Textarea rows={6} value={op.payload} onChange={(e) => setOp({ ...op, payload: e.target.value })} placeholder='{"SoldToParty":"1000"}' className="font-mono text-xs" data-testid="run-payload" /></div>
              )}
            </>
          )}
          <Button onClick={run} disabled={busy} className="gap-2" data-testid="run-execute">{busy ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />} Execute</Button>
          {result && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="run-result">
              <div className={`flex items-center gap-1.5 text-sm font-medium ${result.ok ? "text-emerald-600" : "text-rose-600"}`}>
                {result.ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                {result.ok ? `Success${result.http_status ? ` · HTTP ${result.http_status}` : ""}` : "Failed"}{result.latency_ms != null ? ` · ${result.latency_ms} ms` : ""}
              </div>
              {result.detail && <div className="text-xs text-muted-foreground">{result.detail}</div>}
              {result.response !== undefined && (
                <pre className="text-[11px] bg-[#0b1329] text-[#dbe4f5] rounded-lg p-3 overflow-x-auto font-mono max-h-64">{typeof result.response === "string" ? result.response : JSON.stringify(result.response, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
